import { NewRegistrationFactory } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ConfigurationStorage/NewRegistrationFactory';
import produce from 'immer';
import create, { GetState, SetState, StoreApi } from 'zustand';
import { loadCategories } from './CreateRegistrationUtils';
import createContext from 'zustand/context';

export interface IGalleryStore extends ReturnType<typeof createGallery> {}

export const { Provider: CreateRegistrationStoreProvider, useStore: useRegistrationStore } = createContext<
  StoreApi<IGalleryStore>
>();

export function createGalleryStore() {
  const useStore = create<IGalleryStore>((set, get) => {
    return createGallery(get, set);
  });
  return useStore;
}

export function createGallery(get: GetState<IGalleryStore>, set: SetState<IGalleryStore>) {
  return {
    isCategoriesLoading: false,
    documentationLink: '',
    documentationCategoryText: '',
    selectedCategory: '',
    categories: [] as NewRegistrationFactory[],
    search: '',
    searchCount: null as null | number,
    isSearchLoading: false,
    lastCategory: '',
    selectCategory: (category?: string) => {
      const categoryDocumentationLink = get().categories.filter(c => c.Name === category)[0]?.DocumentationLink;
      const documentationCategoryText = get().categories.filter(c => c.Name === category)[0]?.DocumentationCategoryText;
      set(
        produce<IGalleryStore>(state => {
          state.selectedCategory = category ?? '';
          state.documentationLink = categoryDocumentationLink;
          state.documentationCategoryText = documentationCategoryText;
          state.search = '';
        })
      );
    },
    loadCategories: async (folderId: string, actionKey: string) => {
      const { selectCategory, selectedCategory } = get();
      set(
        produce<IGalleryStore>(state => {
          state.isCategoriesLoading = true;
        })
      );
      const newCategories = await loadCategories(folderId, actionKey);

      set(
        produce<IGalleryStore>(state => {
          state.categories = newCategories;
          state.isCategoriesLoading = false;
        })
      );

      const doNotContainsCategory = newCategories.filter(c => c.Name === selectedCategory && c.HasItems).length === 0;
      if (newCategories.length && (!selectedCategory || doNotContainsCategory)) {
        selectCategory(getFirstVisibleCategory(newCategories));
      }
    },
    setSearch: (search: string) => {
      const { search: oldSearch, categories, selectCategory, lastCategory } = get();
      set(
        produce<IGalleryStore>(state => {
          state.search = search;
        })
      );
      if (oldSearch.length === 0 && search.length > 0) {
        set(
          produce<IGalleryStore>(state => {
            state.lastCategory = state.selectedCategory;
          })
        );
      }
      if (search !== oldSearch) {
        set(
          produce<IGalleryStore>(state => {
            const categoryDocumentationLink = get().categories[0]?.DocumentationLink ?? '';
            const documentationCategoryText = get().categories[0]?.DocumentationCategoryText ?? '';
            state.selectedCategory = search ? `Search Result` : '';
            state.documentationLink = categoryDocumentationLink;
            state.documentationCategoryText = documentationCategoryText;
          })
        );
      }
      if (oldSearch.length && search.length === 0 && categories.length) {
        selectCategory(lastCategory);
      }
    },
    setSearchCount: (count: number) => {
      set(
        produce<IGalleryStore>(state => {
          state.searchCount = count;
          state.isSearchLoading = false;
          if (count > 0) {
            state.selectedCategory = `Search Result${count > 1 ? 's' : ''}`;
          }
        })
      );
    },
    setSearchLoading: (isLoading: boolean) => {
      set(
        produce<IGalleryStore>(state => {
          state.isSearchLoading = isLoading;
          if (isLoading) {
            state.searchCount = null;
          }
        })
      );
    },
  };
}

function getFirstVisibleCategory(categories: NewRegistrationFactory[]): string | undefined {
  const firstCategory = categories.find(item => item.HasItems);
  return firstCategory && firstCategory.Name;
}
